import { useNavigate } from "react-router";
import { useAtom, useSetAtom } from "jotai";
import { ChevronDown, LogOut } from "lucide-react";
import {
  VButton,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuLabel,
  VDropdownMenuSeparator,
  VDropdownMenuTrigger,
  cn,
} from "@/components/ui";
import { roleIcons, roleLabels } from "@/v2/shared/statusMeta";
import { V2_USERS } from "@/v2/auth/users";
import { v2CurrentUserAtom, v2IsAuthenticatedAtom } from "@/v2/auth/store";

export function V2UserMenu(): React.JSX.Element {
  const navigate = useNavigate();
  const [current, setUser] = useAtom(v2CurrentUserAtom);
  const setAuthenticated = useSetAtom(v2IsAuthenticatedAtom);
  const Icon = roleIcons[current.role];

  function handleLogout(): void {
    setAuthenticated(false);
    navigate("/login", { replace: true });
  }

  return (
    <VDropdownMenu>
      <VDropdownMenuTrigger asChild>
        <VButton
          variant="ghost"
          size="md"
          className="h-auto gap-2.5 rounded-lg px-2.5 py-1.5 text-on-primary hover:bg-on-primary/10"
        >
          <span
            className={cn(
              "flex size-7 items-center justify-center rounded-full text-xs font-bold text-white",
              current.avatarColor,
            )}
          >
            {current.firstName[0]}
            {current.lastName[0]}
          </span>
          <span className="hidden text-left md:inline">
            <span className="block font-medium leading-tight">
              {current.firstName} {current.lastName}
            </span>
            <span className="flex items-center gap-1 text-xs text-primary-100">
              <Icon className="size-3" aria-hidden />
              {roleLabels[current.role]}
            </span>
          </span>
          <ChevronDown className="size-4 text-primary-100" aria-hidden />
        </VButton>
      </VDropdownMenuTrigger>
      <VDropdownMenuContent align="end" className="min-w-[16rem]">
        <VDropdownMenuLabel>Wissel van rol (demo)</VDropdownMenuLabel>
        <VDropdownMenuSeparator />
        {V2_USERS.map((u) => {
          const ItemIcon = roleIcons[u.role];
          return (
            <VDropdownMenuItem
              key={u.id}
              onSelect={() => setUser(u)}
              className={cn("gap-3", u.id === current.id && "bg-muted")}
            >
              <span
                className={cn(
                  "flex size-8 items-center justify-center rounded-full text-xs font-bold text-white",
                  u.avatarColor,
                )}
              >
                {u.firstName[0]}
                {u.lastName[0]}
              </span>
              <span className="flex-1">
                <span className="block font-medium text-foreground">
                  {u.firstName} {u.lastName}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ItemIcon className="size-3" aria-hidden />
                  {roleLabels[u.role]} · {u.service}
                </span>
              </span>
            </VDropdownMenuItem>
          );
        })}
        <VDropdownMenuSeparator />
        <VDropdownMenuItem onSelect={handleLogout} className="gap-3">
          <LogOut className="size-4 text-muted-foreground" aria-hidden />
          Uitloggen
        </VDropdownMenuItem>
      </VDropdownMenuContent>
    </VDropdownMenu>
  );
}
